import { z } from 'zod';

import {
  DataSetSchema,
  type Version,
  VersionSchema,
  YesOrNoSchema,
} from './internal';

export type Transaction = z.infer<typeof TransactionSchema>;

export const ISODateTimeSchema = z.string();

export type ISODateTime = z.infer<typeof ISODateTimeSchema>;

export const TransactionSchema = z.object({
  DataSet: DataSetSchema,
  /**
   * Any name here, doesn't matter
   */
  PMAVersion: z.string(),
  /**
   * Any name here, doesn't matter
   */
  PMAName: z.string(),
  /**
   * Combination of Scheme/Administrator/Plan
   *
   * @example 90080
   */
  RoutingCode: z.string(),
  /**
   * Quro Reference Number for an Invoice
   *
   * Can be auto generated when we send the claim - guid is too long.
   * Must be populated and unique for every claim
   */
  InvoiceNum: z.string(),
  SavedDateTime: ISODateTimeSchema,

  /**
   * Any version, we use the API so we can just use that
   *
   * @example API
   */
  MedpraxVersion: z.string().optional(),
});

export type PostalAddress = z.infer<typeof PostalAddressSchema>;

export const PostalAddressSchema = z.object({
  Address: z.string(),
  Suburb: z.string(),
  Town: z.string(),
  Code: z.string(),
});

export type PhysicalAddress = z.infer<typeof PhysicalAddressSchema>;

export const PhysicalAddressSchema = z.object({
  Address: z.string(),
  Suburb: z.string(),
  Town: z.string(),
  Code: z.string(),
});

export interface Demographics {
  WkTelephone: string;
  HMTelephone: string;
  Fax: string;
  Cellphone: string;
  Email: string;
  PostalAddress: PostalAddress;
  PhysicalAddress: PhysicalAddress;
}

export const DemographicsSchema = z.object({
  WkTelephone: z.string(),
  HMTelephone: z.string(),
  Fax: z.string(),
  Cellphone: z.string(),
  Email: z.string(),
  PostalAddress: PostalAddressSchema,
  PhysicalAddress: PhysicalAddressSchema,
});

/**
 * ISO Datetime for when the claim was created (when we stored the original claim in FHIR)
 *
 * @example 2023-03-01T12:00:00
 */

/**
 * ISO Date
 *
 *
 * @example 2023-03-01
 */
export const ISODateSchema = z.string();

export type ISODate = z.infer<typeof ISODateSchema>;

export const BaseProviderSchema = z.object({
  PracticeName: z.string(),
  /**
   * Practice number of the billing entity 7 or 13 digits
   */
  PracticeNum: z.string(),
  /**
   * Specialty code can sometimes be derived from the practice number
   */
  SpecialtyCode: z.string(),

  /**
   * Can just be left as `0`
   */
  SubSpecialtyCode: z.literal('0'),
});

export type BaseProvider = z.infer<typeof BaseProviderSchema>;

export type BillingProvider = z.infer<typeof BillingProviderSchema>;

export const BillingProviderSchema = BaseProviderSchema.extend({
  '@Type': z.literal('Billing'),
});

export type TreatingProvider = z.infer<typeof TreatingProviderSchema>;

export const TreatingProviderSchema = BaseProviderSchema.extend({
  /**
   * 1 treating entity must be populated can be the same as the billing entity if the billing entity is the treating entity
   */
  '@Type': z.literal('Treating'),
  DispensingDoctor: YesOrNoSchema,
  /**
   * HPCSA MP number in the format of `mp1234567`
   */
  HPCSA: z.string(),
});

export type Gender = z.infer<typeof GenderSchema>;

const GenderSchema = z.enum(['Male', 'Female']);

export type Member = z.infer<typeof MemberSchema>;

export const MemberSchema = z.object({
  DOB: ISODateSchema,
  Name: z.string(),
  Surname: z.string(),
  /**
   * Medical aid member number
   */
  MANum: z.string(),
  SchemeName: z.string(),

  Initials: z.string().optional(),
  ID: z.string().optional(),
  Gender: GenderSchema.optional(),
  PlanName: z.string().optional(),
  OptionName: z.string().optional(),
  /**
   * Maybe scheme code - TBC
   */
  SchemeReg: z.string().optional(),
  Demographics: DemographicsSchema.optional(),
});

export type Patient = z.infer<typeof PatientSchema>;

export const PatientSchema = z.object({
  DOB: z.string(),
  Name: z.string(),
  Surname: z.string(),
  Initials: z.string(),
  /**
   * Medical aid dependant code
   */
  DepCode: z.string(),
  /**
   * Financial identifier for customer (if billing patient), for us we can just use the patient id
   */
  AccNum: z.string(),
  /**
   * Is this a newborn baby - always No for us
   */
  NewBornInd: YesOrNoSchema,

  /**
   * Not sure what this is
   */
  Identifier: z.string().optional(),
  Gender: GenderSchema.optional(),
  Demographics: DemographicsSchema.optional(),
});

export type DiagnosisCode = z.infer<typeof DiagnosisCodeSchema>;

export const DiagnosisCodeSchema = z.object({
  Set: z.literal('ICD'),
  Version: z.literal('10'),
  Value: z.string(),
  Description: z.string().optional(),
  /**
   * Primary for the first code, secondary for all subsequent codes
   */
  Type: z.enum(['Primary', 'Secondary']),
});

/**
 * "Client's Injury On Duty" - Compensation fund (https://www.gov.za/services/compensation-fund/register-compensation-fund)
 */
export type COIDFields = z.infer<typeof COIDFieldsSchema>;

export const COIDFieldsSchema = z.object({
  /**
   * This will be `No` for us
   */
  COIDInd: YesOrNoSchema,
  RecInd: z.string().optional(),
  DOI: z.string().optional(),
  EmployerName: z.string().optional(),
  EmployeeNo: z.string().optional(),
  EmployerRegNo: z.string().optional(),
});

export type MoneyCents = z.infer<typeof MoneyCentsSchema>;

export const MoneyCentsSchema = z.string();

export type ClaimFinancials = z.infer<typeof ClaimFinancialsSchema>;

export const ClaimFinancialsSchema = z.object({
  TotalBilledAmt: MoneyCentsSchema,
  TotalDiscount: MoneyCentsSchema,
  NettBilledAmt: MoneyCentsSchema,
});

export type ChargeCode = z.infer<typeof ChargeCodeSchema>;

export const ChargeCodeSchema = z.object({
  '@Type': z.literal('Procedure'),
  Set: z.literal('Tariff'),
  Version: z.literal('1').optional(),
  /**
   * Charge item code
   */
  Value: z.string(),
  /**
   * Charge item title/description
   */
  Description: z.string(),
});

export type LineItemUnits = z.infer<typeof LineItemUnitsSchema>;

const LineItemUnitsSchema = z.literal('Unit');

export enum PlaceServiceCode {
  HomeCare = '12',
}

export type BalancedBilling = z.infer<typeof BalancedBillingSchema>;

export const BalancedBillingSchema = z.object({
  /**
   * Medical liable
   */
  MALiable: MoneyCentsSchema,
  /**
   * Patient liable
   */
  PatLiable: MoneyCentsSchema,
});

export type LineItemFinancials = z.infer<typeof LineItemFinancialsSchema>;

export const LineItemFinancialsSchema = z.object({
  BilledAmt: MoneyCentsSchema,
  GrossAmt: MoneyCentsSchema,

  Discount: MoneyCentsSchema.optional(),
  Levy: MoneyCentsSchema.optional(),
  ContainerFee: MoneyCentsSchema.optional(),
  DispenseFee: MoneyCentsSchema.optional(),
  ServiceFee: MoneyCentsSchema.optional(),
  ProfFee: MoneyCentsSchema.optional(),
  BalancedBilling: BalancedBillingSchema.optional(),
});

export type LineItem = z.infer<typeof LineItemSchema>;

export const LineItemSchema = z.object({
  /**
   * To confirm the types for this - e.g. consumables and procedures
   */
  '@Type': z.literal('Procedure'),
  /**
   * Line number (1 - n)
   *
   * @example '1', '25'
   */
  UniqueID: z.string(),
  ChargeCode: ChargeCodeSchema,
  DOS: ISODateSchema,
  /**
   * Check with TK
   */
  HospInd: YesOrNoSchema,
  /**
   * Place service code - depends on where the procedure took place
   */
  PlaceServiceCode: z.nativeEnum(PlaceServiceCode),
  LineItemFinancials: LineItemFinancialsSchema,
  DiagnosisCodes: DiagnosisCodeSchema.array(),

  /**
   * TBC
   */
  Num: z.string().optional(),
  /**
   * Always Private for us - required for dental or optometry claims
   */
  PricingInd: z.enum(['Private', 'Public']),
  /**
   * Check with TK
   */
  ChargeStart: ISODateTimeSchema.optional(),
  /**
   * Check with TK
   */
  ChargeEnd: ISODateTimeSchema.optional(),
  /**
   * Number of charge item being billed for
   *
   * @example '1', '25'
   */
  ChargeQuantity: z.string().optional(),
  /**
   * Unit to be used to measure the amount of item
   */
  ChargeUnit: LineItemUnitsSchema.optional(),
});

export type Details = z.infer<typeof DetailsSchema>;

export const DetailsSchema = COIDFieldsSchema.extend({
  DiagnosisCodes: DiagnosisCodeSchema.array(),
  /**
   * Total amounts for the claim
   */
  ClaimFinancials: ClaimFinancialsSchema,
  LineItems: LineItemSchema.array(),
  /**
   * Always pay the provider
   */
  PayWho: z.literal('Provider'),
  /**
   * Check with TK
   */
  HospInd: YesOrNoSchema,

  /**
   * TBC what this si
   */
  Num: z.string().optional(),
  /**
   * Practice number of doctor that referred - don't have this
   */
  ReferralNum: z.string().optional(),
  /**
   * Admission Date - Check with TK
   */
  AdmDateTime: ISODateSchema.optional(),
  /**
   * Discharge date - Check with TK
   */
  DisDateTime: ISODateSchema.optional(),
  /**
   * Optometry scripts - we don't prescribe these
   */
  OptomScripts: z.tuple([]).optional(),
});

export const ProvidersSchema = z.tuple([
  BillingProviderSchema,
  TreatingProviderSchema,
]);

export type Providers = z.infer<typeof ProvidersSchema>;

export interface CreateClaimRequest {
  ProviderMessage: {
    '@xmlns:hb': 'healthbridge.co.za/messaging'; // Leave as is, no need to change
    '@Version': Version; // Leave as is, no need to change
    Claim: {
      Transaction: Transaction;
      /**
       * These wll likely be hardcoded as some specific Quro details. The treating provider can be the same as the billing entity if they are the same
       */
      Providers: Providers;
      /**
       * Details of the medical aid main member
       */
      Member: Member;
      Patient: Patient;
      Details: Details;
    };
  };
}

export const CreateClaimRequestSchema = z.object({
  ProviderMessage: z.object({
    '@xmlns:hb': z.literal('healthbridge.co.za/messaging'), // Leave as is, no need to change
    '@Version': VersionSchema, // Leave as is, no need to change
    Claim: z.object({
      Transaction: TransactionSchema,
      /**
       * These wll likely be hardcoded as some specific Quro details. The treating provider can be the same as the billing entity if they are the same
       */
      Providers: ProvidersSchema,
      /**
       * Details of the medical aid main member
       */
      Member: MemberSchema,
      Patient: PatientSchema,
      Details: DetailsSchema,
    }),
  }),
});

export const CreateClaimSuccessResponseSchema = z.object({
  MessageId: z.string(),
  Message: z.string(),
  InvoiceNum: z.string(),
});

const CreateClaimErrorSchema = z.object({
  objectName: z.string(),
  field: z.string(),
  rejectedValue: z.string(),
  codes: z.string().array(),
  defaultMessage: z.string(),
  code: z.string(),
});

export const CreateClaimErrorResponseSchema = z.object({
  Message: z.string(),
  Errors: CreateClaimErrorSchema.array(),
});

export type CreateClaimSuccessResponse = z.infer<
  typeof CreateClaimSuccessResponseSchema
>;

export type CreateClaimErrorResponse = z.infer<
  typeof CreateClaimErrorResponseSchema
>;

export const CreateClaimResponseSchema = z.union([
  CreateClaimSuccessResponseSchema,
  CreateClaimErrorResponseSchema,
]);

export type CreateClaimResponse = z.infer<typeof CreateClaimResponseSchema>;
